import { TextField } from '@wheely/ui-kit';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDismissibleError } from '@/shared/hooks/useDismissibleError';
import { Button } from '@src-common/view/adapters/Button';

import { LinkButton } from '../LinkButton';

import s from './style.module.css';

type Props = {
  /** What was sent and where; rendered above the input. */
  description: React.ReactNode;
  disabled: boolean;
  error?: string;
  attemptsLeft?: number;
  resendAvailableIn?: number;
  onSubmit: (code: string) => void;
  onResend: () => void;
  /** Requests the code by a call; rendered in the footnote when given. */
  onCallMe?: () => void;
};

const useFocus = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  }, []);

  return inputRef;
};

/**
 * One-time code entry shared by every OTP step: sign-in, both registration
 * phases and the phone-conflict handoff. Leaving the step is the parent's
 * business: it renders a Change Email/Phone action at its locked field.
 */
const CodeForm: React.VFC<Props> = ({
  description,
  disabled,
  error,
  attemptsLeft,
  resendAvailableIn,
  onSubmit,
  onResend,
  onCallMe,
}) => {
  const intl = useIntl();
  const [code, setCode] = useState('');
  const inputRef = useFocus();
  // A rejected code stays in the flow state until the next attempt, so the line
  // would sit under the field while the user is already typing the new one.
  const { isDismissed, dismiss, restore } = useDismissibleError();

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      restore();
      onSubmit(code);
    },
    [code, onSubmit, restore],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCode(e.target.value);
      dismiss();
    },
    [dismiss],
  );

  return (
    <div className={s.codeForm}>
      {/* The app validates and phrases every field error itself, in the
          user's language; the browser bubble would say it again, in the
          browser's own, and block the submit before the app is asked. */}
      <form onSubmit={handleSubmit} data-test-id="code-form" noValidate={true}>
        <TextField
          type="number"
          inputRef={inputRef}
          value={code}
          title={
            <FormattedMessage
              defaultMessage="Verification Code"
              id="login_code_title"
              description="Code field label"
            />
          }
          description={description}
          onChange={handleChange}
          placeholder={intl.formatMessage({
            defaultMessage: 'Code',
            id: 'A68Nq+',
            description: 'Pin code placeholder',
          })}
          disabled={disabled}
          error={isDismissed ? undefined : error}
          autoFocus={true}
        />
        <p className={s.resend} data-test-id="code-form__resend">
          <FormattedMessage
            defaultMessage="Haven't received the code?"
            id="login_code_helper"
            description="Resend footnote lead"
          />{' '}
          {resendAvailableIn === undefined ? (
            <>
              {onCallMe && (
                <>
                  <LinkButton disabled={disabled} onClick={onCallMe}>
                    <FormattedMessage
                      defaultMessage="Call me"
                      id="login_code_call_me"
                      description="Call me action"
                    />
                  </LinkButton>{' '}
                  <FormattedMessage
                    defaultMessage="or"
                    id="login_code_or"
                    description="Resend actions separator"
                  />{' '}
                </>
              )}
              <LinkButton disabled={disabled} onClick={onResend}>
                <FormattedMessage
                  defaultMessage="Resend"
                  id="login_code_resend"
                  description="Resend code button title"
                />
              </LinkButton>
            </>
          ) : (
            <FormattedMessage
              defaultMessage="Resend in {seconds}s"
              id="login_code_resend_cooldown"
              description="Resend cooldown note"
              values={{ seconds: resendAvailableIn }}
            />
          )}
        </p>
        <div className={s.actions}>
          <Button isDisabled={disabled || !code} type="submit">
            <FormattedMessage
              defaultMessage="Continue"
              id="global_continue"
              description="Continue button title"
            />
          </Button>
        </div>
        {attemptsLeft !== undefined && (
          <p className={s.attempts}>
            <FormattedMessage
              defaultMessage="Attempts left: {attemptsLeft}"
              id="login_code_attempts_left"
              description="OTP attempts left note"
              values={{ attemptsLeft }}
            />
          </p>
        )}
      </form>
    </div>
  );
};

export { CodeForm };
